import { createContext, useContext } from 'react';
import { AuthUser, FetchUserAttributesOutput } from 'aws-amplify/auth';
import { type IImport } from '@/types/import';

export type Person = AuthUser & FetchUserAttributesOutput;
export type MailersRole = 'admin' | 'manager' | 'user';
export type MailersSearchAccess = 'active' | 'inactive';

export interface MailersPermissions {
  canSearchMailers: boolean;
  canClaimLead: boolean;
  canViewOwnLeads: boolean;
  canExportOwnLeads: boolean;
  canViewTeamUsers: boolean;
  canViewTeamLeads: boolean;
  canAssignWithinTeam: boolean;
  canManageAllAssignments: boolean;
  canManageUsers: boolean;
  canManageTeams: boolean;
  canToggleTeamUserSearchAccess: boolean;
}

export interface MailersSession {
  ok: boolean;
  area: 'mailers';
  name: 'session';
  user: {
    userId: string;
    sub: string;
    email: string;
    displayName: string | null;
    role: MailersRole;
    status: string;
    searchAccess: MailersSearchAccess;
    seedMatched: boolean | null;
    scopes: string[];
    bonzoWebhookUrlConfigured: boolean;
    bonzoApiKeyConfigured: boolean;
    bonzoIntroSmsConfigured: boolean;
  };
  team: {
    teamId: string;
    name: string;
    managerUserId: string | null;
    managerEmail: string | null;
    status: string | null;
  } | null;
  permissions: MailersPermissions;
}

export interface AppContextType {
  retrieveUrl: string;
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  userHasAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  currentUser: Person;
  setCurrentUser: React.Dispatch<React.SetStateAction<Person>>;
  imports: IImport[];
  setImports: React.Dispatch<React.SetStateAction<IImport[]>>;
  mailersSession: MailersSession | null;
  setMailersSession: React.Dispatch<React.SetStateAction<MailersSession | null>>;
  isMailersSessionLoading: boolean;
}

export const AppContext = createContext<AppContextType>({
  retrieveUrl: '',
  isAuthenticating: false,
  isAuthenticated: false,
  userHasAuthenticated: useAppContext,
  currentUser: {} as Person,
  setCurrentUser: useAppContext,
  imports: [],
  setImports: useAppContext,
  mailersSession: null,
  setMailersSession: useAppContext,
  isMailersSessionLoading: true,
});

export function useAppContext() {
  return useContext(AppContext);
}
